/**
 * Device capability detection for the 3D layer.
 *
 *   high — desktop / fine pointer: full interactive 3D
 *   low  — mobile / touch / small screens: lightweight 3D, no parallax, fewer particles
 *   none — no WebGL, data saver, very low memory/cores: static CSS/SVG fallbacks only
 *
 * Software rasterisers (SwiftShader, llvmpipe…) count as `none`: they render WebGL on the
 * CPU and would block the main thread every frame.
 *
 * QA override: append `?3d=off|low|high` to the URL (also disables the runtime frame watchdog).
 */
export type RenderTier = 'high' | 'low' | 'none';

export interface Capability {
  tier: RenderTier;
  reducedMotion: boolean;
  isTouch: boolean;
  /** Tier was forced through `?3d=` — skip automatic downgrades. */
  forced: boolean;
}

export interface QualityPreset {
  dpr: [number, number];
  antialias: boolean;
  /** Multiplier applied to every particle count. */
  particles: number;
  /** Mouse-driven camera parallax. */
  parallax: boolean;
  /** Instance multiplier for crowds (buildings, trees, field rows). */
  detail: number;
}

export const qualityPresets: Record<Exclude<RenderTier, 'none'>, QualityPreset> = {
  high: { dpr: [1, 1.75], antialias: true, particles: 1, parallax: true, detail: 1 },
  low: { dpr: [1, 1.5], antialias: false, particles: 0.35, parallax: false, detail: 0.6 },
};

const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software|basic render/i;

/** True when a hardware-accelerated WebGL context is available. */
function hasHardwareWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ||
      canvas.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) return false;
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = info ? String(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) : '';
    // Release the probe context immediately so it doesn't count against the browser limit.
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return !SOFTWARE_RENDERER.test(renderer);
  } catch {
    return false;
  }
}

export function detectCapability(): Capability {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(pointer: coarse)').matches;

  const override = new URLSearchParams(window.location.search).get('3d');
  if (override === 'off') return { tier: 'none', reducedMotion, isTouch, forced: true };
  if (override === 'low' || override === 'high') {
    return { tier: override, reducedMotion, isTouch, forced: true };
  }

  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { saveData?: boolean };
  };
  const saveData = nav.connection?.saveData === true;
  const lowMemory = typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 2;
  const lowCores = typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency <= 2;

  if (saveData || lowMemory || lowCores || !hasHardwareWebGL()) {
    return { tier: 'none', reducedMotion, isTouch, forced: false };
  }

  const small = window.innerWidth < 768;
  return { tier: isTouch || small ? 'low' : 'high', reducedMotion, isTouch, forced: false };
}
